import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ActionsBar from "./ActionsBar";
import LeadCell from "./LeadCell";
import StatusPill from "./StatusPill";

function Table({
  columns,
  rows,
  onRowClick,
  hideViewAction = false,
  onSelectionChange,
  onArchiveRow,
  onRestoreRow,
  onInvoiceRow,
  resetSelectionKey,
  enableSelection = true,
  enableSorting = true,
  stackOnMobile = false,
  defaultSelectedIds = null,
}) {
  const columnTemplates = columns.map((col) => col.width || "minmax(120px, 1fr)").join(" ");
  const template = enableSelection ? `46px ${columnTemplates}` : columnTemplates;
  const tableClassName = stackOnMobile ? "table stackable" : "table";
  const selectAllRef = useRef(null);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [sortState, setSortState] = useState({ key: null, direction: "asc" });

  const parseNumeric = useCallback((value) => {
    if (value instanceof Date) return value.getTime();
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      const cleaned = value.replace(/[^0-9.-]+/g, "");
      if (cleaned && /[0-9]/.test(cleaned)) {
        const numeric = Number(cleaned);
        if (!Number.isNaN(numeric)) return numeric;
      }
    }
    return null;
  }, []);

  const getSortValue = useCallback(
    (row, col) => {
      const raw = typeof col.sortValue === "function" ? col.sortValue(row) : row[col.key];
      const candidate = raw?.sortValue !== undefined ? raw.sortValue : raw;
      if (candidate === null || candidate === undefined) return null;

      const numeric = parseNumeric(candidate);
      if (numeric !== null) return numeric;

      if (candidate && typeof candidate === "object") {
        if (candidate.label) return candidate.label;
        if (candidate.title) return candidate.title;
        if (candidate.value !== undefined) return candidate.value;
        if (candidate.display !== undefined) return candidate.display;
      }

      return String(candidate).toLowerCase();
    },
    [parseNumeric]
  );

  const sortedRows = useMemo(() => {
    if (!enableSorting || !sortState.key) return rows;
    const targetColumn = columns.find((col) => col.key === sortState.key);
    if (!targetColumn) return rows;
    const direction = sortState.direction === "desc" ? -1 : 1;
    return [...rows].sort((a, b) => {
      const aValue = getSortValue(a, targetColumn);
      const bValue = getSortValue(b, targetColumn);
      const aMissing = aValue === null || aValue === undefined || aValue === "";
      const bMissing = bValue === null || bValue === undefined || bValue === "";
      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;
      if (bMissing) return -1;
      if (typeof aValue === "number" && typeof bValue === "number") {
        return (aValue - bValue) * direction;
      }
      return String(aValue).localeCompare(String(bValue)) * direction;
    });
  }, [columns, enableSorting, rows, sortState.direction, sortState.key, getSortValue]);

  const toggleSort = (col) => {
    if (!enableSorting || col.sortable === false) return;
    setSortState((prev) => {
      if (prev.key !== col.key) return { key: col.key, direction: "asc" };
      if (prev.direction === "asc") return { key: col.key, direction: "desc" };
      return { key: null, direction: "asc" };
    });
  };

  const rowIds = useMemo(
    () => sortedRows.map((row, idx) => row.id ?? row.refCode ?? row.sku ?? row.key ?? row.name ?? idx),
    [sortedRows]
  );

  const handleRowClick = (row) => (event) => {
    if (!onRowClick) return;
    const target = event?.target;
    if (target?.closest("button, a, input, select, textarea")) return;
    onRowClick(row);
  };

  useEffect(() => {
    setSelectedRows((prev) => {
      const next = new Set();
      rowIds.forEach((id) => {
        if (prev.has(id)) next.add(id);
      });
      return next;
    });
  }, [rowIds]);

  useEffect(() => {
    if (!enableSelection) {
      setSelectedRows(new Set());
    }
  }, [enableSelection]);

  const allSelected = enableSelection && rowIds.length > 0 && selectedRows.size === rowIds.length;
  const isIndeterminate = enableSelection && selectedRows.size > 0 && selectedRows.size < rowIds.length;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  const toggleSelectAll = (event) => {
    if (!enableSelection) return;
    event.stopPropagation();
    setSelectedRows((prev) => {
      if (prev.size === rowIds.length) return new Set();
      return new Set(rowIds);
    });
  };

  const toggleRow = (rowId) => (event) => {
    if (!enableSelection) return;
    event.stopPropagation();
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        next.add(rowId);
      }
      return next;
    });
  };

  useEffect(() => {
    if (!enableSelection) return;
    if (!onSelectionChange) return;
    const selectedRowsData = sortedRows.filter((_, idx) => selectedRows.has(rowIds[idx]));
    onSelectionChange({ ids: new Set(selectedRows), rows: selectedRowsData });
  }, [enableSelection, selectedRows, onSelectionChange, rowIds, sortedRows]);

  useEffect(() => {
    setSelectedRows(new Set());
  }, [resetSelectionKey]);

  useEffect(() => {
    if (!enableSelection) return;
    if (!defaultSelectedIds) return;
    const rawIds = Array.isArray(defaultSelectedIds)
      ? defaultSelectedIds
      : defaultSelectedIds instanceof Set
      ? Array.from(defaultSelectedIds)
      : [];
    const normalized = new Set(rawIds.map((id) => `${id}`));
    if (!normalized.size) return;
    const next = new Set();
    rowIds.forEach((id) => {
      if (normalized.has(`${id}`)) {
        next.add(id);
      }
    });
    if (next.size) {
      setSelectedRows(next);
    }
  }, [defaultSelectedIds, enableSelection, rowIds, resetSelectionKey]);

  const getExpiryDate = (row, value) => {
    const raw =
      (value && typeof value === "object" ? value.raw || value.value || value.label : null) ||
      row?.product?.expiryDate ||
      row?.expiryDate ||
      (typeof value === "string" ? value : null) ||
      row?.expiry;
    if (!raw) return null;
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const formatExpiryLabel = (value, expiryDate) => {
    if (value && typeof value === "object") {
      if (value.label) return value.label;
      if (typeof value.value === "string") return value.value;
    }
    if (typeof value === "string" && value.trim()) return value;
    if (expiryDate) {
      return expiryDate.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    }
    return "—";
  };

  return (
    <div className={tableClassName}>
      <div className="table-head">
        <div className="table-row header" style={{ "--table-template": template }}>
          {enableSelection ? (
            <div className="cell checkbox" data-label={stackOnMobile ? "Select" : undefined}>
              <input
                ref={selectAllRef}
                className="table-checkbox"
                type="checkbox"
                aria-label="Select all rows"
                checked={allSelected}
                onChange={toggleSelectAll}
              />
            </div>
          ) : null}
          {columns.map((col) => {
            const sortable = enableSorting && col.sortable !== false;
            const isSorted = sortable && sortState.key === col.key;
            const headerLabel = col.label || col.key || "";
            const indicator = !sortable ? null : (
              <span className={`sort ${isSorted ? "active" : ""}`}>{isSorted ? (sortState.direction === "asc" ? "↑" : "↓") : "↕"}</span>
            );
            return (
              <div
                key={col.key}
                className={`cell ${col.align || ""} ${sortable ? "sortable" : ""}`}
                data-label={headerLabel}
                role={sortable ? "button" : undefined}
                tabIndex={sortable ? 0 : undefined}
                onClick={() => toggleSort(col)}
                onKeyDown={(event) => {
                  if (!sortable) return;
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    toggleSort(col);
                  }
                }}
              >
                <span className="header-label">{col.label}</span> {indicator}
              </div>
            );
          })}
        </div>
      </div>
      <div className="table-body">
        {sortedRows.map((row, idx) => {
          const rowId = rowIds[idx];
          return (
            <div
              className={`table-row ${onRowClick ? "clickable" : ""}`}
              key={rowId}
              style={{ "--table-template": template }}
              onClick={handleRowClick(row)}
            >
              {enableSelection ? (
                <div className="cell checkbox" data-label="Select">
                  <input
                    className="table-checkbox"
                    type="checkbox"
                    aria-label={`Select ${row.name || "row"}`}
                    checked={selectedRows.has(rowId)}
                    onChange={toggleRow(rowId)}
                  />
                </div>
              ) : null}
              {columns.map((col) => {
                const value = row[col.key];
                const cellLabel = col.label || col.key || "";
                if (value?.type === "lead") {
                  return (
                    <div className={`cell lead-cell ${col.align || ""}`} key={col.key} data-label={cellLabel}>
                      <LeadCell data={value} />
                    </div>
                  );
                }
                if (value?.type === "image") {
                  return (
                    <div className={`cell ${col.align || ""}`} key={col.key} data-label={cellLabel}>
                      <div className="table-thumb">
                        {value.url ? <img src={value.url} alt={value.alt || "Thumbnail"} /> : null}
                      </div>
                    </div>
                  );
                }
                if (value?.type === "status") {
                  return (
                    <div className={`cell ${col.align || ""}`} key={col.key} data-label={cellLabel}>
                      <StatusPill label={value.label} tone={value.tone} />
                    </div>
                  );
                }
                if (col.key === "expiry") {
                  const expiryDate = getExpiryDate(row, value);
                  const isExpired = expiryDate ? expiryDate.getTime() < Date.now() : false;
                  const label = formatExpiryLabel(value, expiryDate);
                  return (
                    <div className={`cell ${col.align || ""}`} key={col.key} data-label={cellLabel}>
                      <div className="expiry-cell">
                        <span className="expiry-label">{label}</span>
                        {isExpired ? <StatusPill label="Expired" tone="danger" /> : null}
                      </div>
                    </div>
                  );
                }
                if (col.key === "action") {
                  const rowData = row.product ?? row;
                  const isArchived =
                    Boolean(rowData?.archivedAt) ||
                    rowData?.lifecycleStatus === "inactive" ||
                    rowData?.status === "inactive";
                  return (
                    <div className={`cell action ${col.align || ""}`} key={col.key} data-label={cellLabel}>
                      <ActionsBar
                        hideView={hideViewAction}
                        archived={isArchived}
                        onArchive={onArchiveRow ? () => onArchiveRow(rowData) : undefined}
                        onRestore={onRestoreRow ? () => onRestoreRow(rowData) : undefined}
                        onInvoice={onInvoiceRow ? () => onInvoiceRow(rowData) : undefined}
                      />
                    </div>
                  );
                }
                const content = value && typeof value === "object" && value.display !== undefined ? value.display : value;
                return (
                  <div className={`cell ${col.align || ""}`} key={col.key} data-label={cellLabel}>
                    {content}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default Table;
